import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import Ionicons from "@expo/vector-icons/Ionicons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import {
  MONTHLY_VIBE_CARD_SIZE,
  MonthlyVibeCardRenderer,
} from "@/components/share/monthly-vibe-card";
import { monthlyVibeCardApi } from "@/api";
import { notify } from "@/lib/dialog";
import { monthLabel } from "@/lib/month-label";
import { saveImage, shareImage } from "@/lib/share-image";
import { vibeCardPanel } from "@/lib/vibe-card-panel";
import { useAppTheme, type AppColors } from "@/theme/app-theme";
import type { MonthlyVibeCard } from "@/types";

export default function VibeCardScreen() {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const { colors, statusBarStyle } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const params = useLocalSearchParams<{ period?: string }>();

  const [period, setPeriod] = useState<string | null>(params.period ?? null);
  const [card, setCard] = useState<MonthlyVibeCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [revealing, setRevealing] = useState(false);
  const [genError, setGenError] = useState(false);
  const [busy, setBusy] = useState(false);

  const exportRef = useRef<View>(null);

  const content = card?.status === "available" ? card.content : null;
  const monthName = period ? monthLabel(period, i18n.language) : "";
  const previewWidth = Math.min(width - 32, 380);
  const scale = previewWidth / MONTHLY_VIBE_CARD_SIZE;

  // Resolve which month to show (explicit param, else the latest completed).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let targetPeriod = params.period ?? null;
        if (!targetPeriod) {
          const status = await monthlyVibeCardApi.status();
          targetPeriod = status.latest.periodKey;
          if (!cancelled) setCard(status.latest);
        }
        if (!targetPeriod) return;
        if (!cancelled) setPeriod(targetPeriod);
        const c = await monthlyVibeCardApi.getForPeriod(targetPeriod);
        if (!cancelled) setCard(c);
      } catch {
        // Deliberately silent: `card` stays null and the backstop panel below
        // explains it. The comment here used to say "leave loading; UI shows a
        // generic message" — both halves were false. `finally` clears loading,
        // and no such message existed, so every failure on this path rendered
        // an empty screen. (`status.latest` is also read unguarded above, so a
        // null `latest` lands here too.)
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params.period]);

  useEffect(() => {
    if (!period || card?.status !== "generating") return;
    let cancelled = false;
    const timer = setInterval(() => {
      monthlyVibeCardApi
        .getForPeriod(period)
        .then((result) => {
          if (!cancelled) setCard(result);
        })
        .catch(() => undefined);
    }, 1_500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [card?.status, period]);

  const reveal = useCallback(async () => {
    if (!period || revealing) return;
    setRevealing(true);
    setGenError(false);
    try {
      const result = await monthlyVibeCardApi.reveal(period);
      setCard(result);
      // `expired` is an answer, not a failure — the month's jar was cleared
      // after its reveal window closed, and the panel below explains it.
      if (
        result.status !== "available" &&
        result.status !== "insufficient_data" &&
        result.status !== "expired" &&
        result.status !== "generating"
      ) {
        setGenError(true);
      }
    } catch {
      setGenError(true);
    } finally {
      setRevealing(false);
    }
  }, [period, revealing]);

  const capture = useCallback(async () => {
    if (!exportRef.current) throw new Error("Vibe card is not ready.");
    // Imported here rather than at module scope: on web this pulls html2canvas,
    // which was 196 kB of the entry bundle paid by every route — including the
    // legal pages, which never capture anything. Expo splits web bundles on
    // async imports, so this is what moves it into a chunk that loads when
    // someone actually shares a card.
    const { captureRef } = await import("react-native-view-shot");
    return captureRef(exportRef, {
      format: "png",
      quality: 1,
      result: Platform.OS === "web" ? "data-uri" : "tmpfile",
      width: MONTHLY_VIBE_CARD_SIZE,
      height: MONTHLY_VIBE_CARD_SIZE,
    });
  }, []);

  async function onShare() {
    if (busy) return;
    setBusy(true);
    try {
      const uri = await capture();
      await shareImage(uri, filename(period), {
        dialogTitle: t("profile.monthlyVibeCard.editor.share"),
        text: content?.headline ?? "",
      });
    } catch {
      notify(t("profile.monthlyVibeCard.generator.error"));
    } finally {
      setBusy(false);
    }
  }

  async function onSave() {
    if (busy) return;
    setBusy(true);
    try {
      const uri = await capture();
      await saveImage(uri, filename(period), {
        dialogTitle: t("profile.monthlyVibeCard.editor.save"),
      });
    } catch {
      notify(t("profile.monthlyVibeCard.generator.error"));
    } finally {
      setBusy(false);
    }
  }

  /**
   * ONE decision about what this screen shows, made in a pure function so the
   * surface can be checked for completeness (`lib/vibe-card-panel.ts`,
   * `tests/mobile/vibe-card-panel.test.ts`).
   *
   * It used to be a chain of scattered `status === …` conditionals, and two of
   * the eight statuses matched none of them: staging ran with the feature flag
   * off, so every month came back `unavailable` and every user got this header
   * over an empty body. A chain cannot be checked for completeness; a total
   * function can.
   */
  const panel = vibeCardPanel(card?.status, {
    hasContent: !!content,
    revealing,
  });
  const isAvailable = panel === "preview";

  return (
    <SafeAreaView style={styles.safe} edges={["top", "bottom"]}>
      <StatusBar style={statusBarStyle} />

      {/* Hidden full-size export host (only when there's a card to capture). */}
      {content ? (
        <View style={styles.exportHost} pointerEvents="none">
          <View ref={exportRef} collapsable={false} style={styles.exportFrame}>
            <MonthlyVibeCardRenderer content={content} />
          </View>
        </View>
      ) : null}

      <View style={styles.header}>
        <Pressable
          onPress={() => router.replace("/profile")}
          accessibilityRole="button"
          accessibilityLabel={t("profile.monthlyVibeCard.editor.close")}
          hitSlop={10}
          style={styles.headerBtn}
        >
          <Ionicons name="close" size={24} color={colors.text} />
        </Pressable>
        <Text style={styles.headerTitle} numberOfLines={1}>
          {monthName
            ? t("profile.monthlyVibeCard.entry.availableTitle", {
                month: monthName,
              })
            : t("profile.monthlyVibeCard.entry.title")}
        </Text>
        <View style={styles.headerBtn} />
      </View>

      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator color={colors.primary} />
        </View>
      ) : (
        <ScrollView
          contentContainerStyle={[
            styles.body,
            isAvailable && styles.bodyWithActions,
          ]}
          showsVerticalScrollIndicator={false}
        >
          {isAvailable && content ? (
            <View
              style={[
                styles.previewBox,
                { width: previewWidth, height: previewWidth },
              ]}
              accessible
              accessibilityLabel={`${content.headline}. ${content.subline}`}
            >
              <View
                style={{
                  width: MONTHLY_VIBE_CARD_SIZE,
                  height: MONTHLY_VIBE_CARD_SIZE,
                  transform: [{ scale }],
                  transformOrigin: "top left",
                }}
              >
                <MonthlyVibeCardRenderer content={content} />
              </View>
            </View>
          ) : null}

          {/* Reveal — the only place AI cost is incurred. */}
          {panel === "reveal" ? (
            <View style={styles.panel}>
              <Text style={styles.panelTitle}>
                {t("profile.monthlyVibeCard.entry.readyTitle", {
                  month: monthName,
                })}
              </Text>
              <Text style={styles.panelSubtitle}>
                {t("profile.monthlyVibeCard.entry.readySubtitle")}
              </Text>
              {genError ? (
                <Text style={styles.noticeError}>
                  {t("profile.monthlyVibeCard.generator.error")}
                </Text>
              ) : null}
              <Pressable
                onPress={reveal}
                disabled={revealing}
                accessibilityRole="button"
                accessibilityLabel={t("profile.monthlyVibeCard.entry.reveal")}
                style={[styles.revealBtn, revealing && styles.btnBusy]}
              >
                {revealing ? (
                  <>
                    <ActivityIndicator color="#fff" size="small" />
                    <Text style={styles.revealText}>
                      {t("profile.monthlyVibeCard.generator.generating")}
                    </Text>
                  </>
                ) : (
                  <>
                    <Ionicons name="gift" size={18} color="#fff" />
                    <Text style={styles.revealText}>
                      {t("profile.monthlyVibeCard.entry.reveal")}
                    </Text>
                  </>
                )}
              </Pressable>
            </View>
          ) : null}

          {panel === "generating" ? (
            <View style={styles.panel}>
              <ActivityIndicator color={colors.primary} />
              <Text style={styles.panelSubtitle}>
                {t("profile.monthlyVibeCard.generator.generating")}
              </Text>
            </View>
          ) : panel === "failed" ? (
            <View style={styles.panel}>
              <Text style={styles.panelTitle}>
                {t("profile.monthlyVibeCard.generator.failedTitle")}
              </Text>
              <Text style={styles.panelSubtitle}>
                {t("profile.monthlyVibeCard.generator.failedSubtitle")}
              </Text>
            </View>
          ) : null}

          {/* Insufficient data. */}
          {panel === "insufficient" ? (
            <View style={styles.panel}>
              <Text style={styles.panelTitle}>
                {t("profile.monthlyVibeCard.entry.insufficientTitle")}
              </Text>
              <Text style={styles.panelSubtitle}>
                {t("profile.monthlyVibeCard.entry.insufficientSubtitle")}
              </Text>
              <Pressable
                onPress={() => router.push("/mood-jar")}
                accessibilityRole="button"
                accessibilityLabel={t("profile.monthlyVibeCard.entry.addMoods")}
                style={styles.secondaryPanelBtn}
              >
                <Ionicons
                  name="add-circle-outline"
                  size={18}
                  color={colors.primary}
                />
                <Text style={styles.secondaryPanelText}>
                  {t("profile.monthlyVibeCard.entry.addMoods")}
                </Text>
              </Pressable>
            </View>
          ) : null}

          {/* Window closed — the jar for that month has been cleared. Points at
              this month rather than dwelling on the one that got away. */}
          {panel === "expired" ? (
            <View style={styles.panel}>
              <Text style={styles.panelTitle}>
                {t("profile.monthlyVibeCard.entry.expiredTitle")}
              </Text>
              <Text style={styles.panelSubtitle}>
                {t("profile.monthlyVibeCard.entry.expiredSubtitle")}
              </Text>
              <Pressable
                onPress={() => router.push("/mood-jar")}
                accessibilityRole="button"
                accessibilityLabel={t("profile.monthlyVibeCard.entry.addMoods")}
                style={styles.secondaryPanelBtn}
              >
                <Ionicons
                  name="add-circle-outline"
                  size={18}
                  color={colors.primary}
                />
                <Text style={styles.secondaryPanelText}>
                  {t("profile.monthlyVibeCard.entry.addMoods")}
                </Text>
              </Pressable>
            </View>
          ) : null}

          {/* The month is still running — there is nothing to reveal yet, and
              saying so is the whole point of opening this screen early. */}
          {panel === "collecting" ? (
            <View style={styles.panel}>
              <Text style={styles.panelTitle}>
                {t("profile.monthlyVibeCard.entry.collectingTitle")}
              </Text>
              <Text style={styles.panelSubtitle}>
                {t("profile.monthlyVibeCard.entry.collectingSubtitle")}
              </Text>
              <Pressable
                onPress={() => router.push("/mood-jar")}
                accessibilityRole="button"
                accessibilityLabel={t("profile.monthlyVibeCard.entry.addMoods")}
                style={styles.secondaryPanelBtn}
              >
                <Ionicons
                  name="add-circle-outline"
                  size={18}
                  color={colors.primary}
                />
                <Text style={styles.secondaryPanelText}>
                  {t("profile.monthlyVibeCard.entry.addMoods")}
                </Text>
              </Pressable>
            </View>
          ) : null}

          {/* No card is possible for this month — a period that has not
              happened yet, or the kill switch being off. Both are "not now"
              rather than an error, so it points at the jar like the others. */}
          {panel === "unavailable" ? (
            <View style={styles.panel}>
              <Text style={styles.panelTitle}>
                {t("profile.monthlyVibeCard.entry.unavailableTitle")}
              </Text>
              <Text style={styles.panelSubtitle}>
                {t("profile.monthlyVibeCard.entry.unavailableSubtitle")}
              </Text>
              <Pressable
                onPress={() => router.push("/mood-jar")}
                accessibilityRole="button"
                accessibilityLabel={t("profile.monthlyVibeCard.entry.addMoods")}
                style={styles.secondaryPanelBtn}
              >
                <Ionicons
                  name="add-circle-outline"
                  size={18}
                  color={colors.primary}
                />
                <Text style={styles.secondaryPanelText}>
                  {t("profile.monthlyVibeCard.entry.addMoods")}
                </Text>
              </Pressable>
            </View>
          ) : null}

          {/* The backstop. `explained` is the complement of every panel above,
              so this covers a null card — which is what the loader's silent
              catch leaves behind on ANY API failure, and what its stale
              comment already claimed was handled — as well as any status added
              to the union later. Whatever happens, the body is never empty. */}
          {panel === "unknown" ? (
            <View style={styles.panel}>
              <Text style={styles.panelTitle}>
                {t("profile.monthlyVibeCard.entry.unknownTitle")}
              </Text>
              <Text style={styles.panelSubtitle}>
                {t("profile.monthlyVibeCard.entry.unknownSubtitle")}
              </Text>
            </View>
          ) : null}
        </ScrollView>
      )}

      {/* Save / Share — free & unlimited; only once a card exists. */}
      {isAvailable ? (
        <View style={styles.actions}>
          <Pressable
            onPress={onSave}
            disabled={busy}
            accessibilityRole="button"
            accessibilityLabel={t("profile.monthlyVibeCard.editor.save")}
            style={[
              styles.actionBtn,
              styles.secondaryBtn,
              busy && styles.btnBusy,
            ]}
          >
            <Ionicons
              name="download-outline"
              size={20}
              color={colors.primary}
            />
            <Text style={styles.secondaryText}>
              {t("profile.monthlyVibeCard.editor.save")}
            </Text>
          </Pressable>
          <Pressable
            onPress={onShare}
            disabled={busy}
            accessibilityRole="button"
            accessibilityLabel={t("profile.monthlyVibeCard.editor.share")}
            style={[
              styles.actionBtn,
              styles.primaryBtn,
              busy && styles.btnBusy,
            ]}
          >
            {busy ? (
              <ActivityIndicator color="#fff" size="small" />
            ) : (
              <Ionicons name="share-social-outline" size={20} color="#fff" />
            )}
            <Text style={styles.primaryText}>
              {t("profile.monthlyVibeCard.editor.share")}
            </Text>
          </Pressable>
        </View>
      ) : null}
    </SafeAreaView>
  );
}

function filename(period: string | null): string {
  return `opncue-vibe-card-${period ?? new Date().toISOString().slice(0, 7)}.png`;
}

function createStyles(c: AppColors) {
  return StyleSheet.create({
    safe: { flex: 1, backgroundColor: c.background },
    exportHost: {
      position: "absolute",
      width: 1,
      height: 1,
      overflow: "hidden",
    },
    exportFrame: {
      width: MONTHLY_VIBE_CARD_SIZE,
      height: MONTHLY_VIBE_CARD_SIZE,
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.border,
    },
    headerBtn: {
      width: 40,
      height: 40,
      alignItems: "center",
      justifyContent: "center",
    },
    headerTitle: {
      flex: 1,
      textAlign: "center",
      fontSize: 17,
      fontWeight: "700",
      color: c.text,
    },
    centered: { flex: 1, alignItems: "center", justifyContent: "center" },
    body: { padding: 16, paddingBottom: 28, alignItems: "center", gap: 16 },
    bodyWithActions: { paddingBottom: 98 },
    previewBox: {
      borderRadius: 24,
      overflow: "hidden",
      backgroundColor: c.surfaceMuted,
    },
    panel: {
      width: "100%",
      maxWidth: 420,
      gap: 12,
      padding: 16,
      borderRadius: 18,
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: c.surface,
    },
    panelTitle: { fontSize: 16, fontWeight: "700", color: c.text },
    panelSubtitle: { fontSize: 13, color: c.textSecondary, lineHeight: 18 },
    noticeError: { fontSize: 13, color: c.danger, lineHeight: 18 },
    revealBtn: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      height: 50,
      borderRadius: 14,
      backgroundColor: c.primary,
    },
    revealText: { fontSize: 16, fontWeight: "700", color: "#fff" },
    secondaryPanelBtn: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      height: 44,
      borderRadius: 12,
      backgroundColor: c.surfaceMuted,
      borderWidth: 1,
      borderColor: c.border,
    },
    secondaryPanelText: { fontSize: 14, fontWeight: "700", color: c.primary },
    actions: {
      flexDirection: "row",
      gap: 10,
      paddingHorizontal: 16,
      paddingTop: 10,
      paddingBottom: Platform.OS === "ios" ? 6 : 12,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.border,
      backgroundColor: c.surface,
    },
    actionBtn: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 7,
      height: 50,
      borderRadius: 14,
    },
    secondaryBtn: {
      flex: 1,
      backgroundColor: c.surfaceMuted,
      borderWidth: 1,
      borderColor: c.border,
    },
    secondaryText: { fontSize: 15, fontWeight: "700", color: c.primary },
    primaryBtn: { flex: 1.3, backgroundColor: c.primary },
    primaryText: { fontSize: 15, fontWeight: "700", color: "#fff" },
    btnBusy: { opacity: 0.7 },
  });
}
